import { useEffect, useState, useCallback, useRef } from 'react';
import { Room, RemoteParticipant, RemoteTrackPublication } from 'twilio-video';
import useVideoContext from '../useVideoContext/useVideoContext';

export interface TrackStats {
  trackId: string;
  trackType: 'audio' | 'video';
  bitrate: number;
  packetsLost: number;
  packetsReceived: number;
  packetLossPercentage: number;
  jitter?: number;
  roundTripTime?: number;
}

export interface ParticipantStats {
  participantSid: string;
  participantIdentity: string;
  isLocal: boolean;
  tracks: TrackStats[];
  overallQuality: 'excellent' | 'good' | 'fair' | 'poor';
}

export interface RoomStats {
  timestamp: number;
  localParticipant: ParticipantStats | null;
  remoteParticipants: ParticipantStats[];
  aggregate: {
    totalBitrate: number;
    avgPacketLoss: number;
    avgRoundTripTime: number;
    avgJitter: number;
  };
}

interface PreviousStats {
  [trackId: string]: {
    bytesReceived: number;
    bytesSent: number;
    packetsReceived: number;
    packetsLost: number;
    timestamp: number;
  };
}

const QUALITY_THRESHOLDS = {
  excellent: { packetLoss: 0.5, rtt: 100, jitter: 30 },
  good: { packetLoss: 2, rtt: 200, jitter: 50 },
  fair: { packetLoss: 5, rtt: 400, jitter: 100 },
};

function calculateQuality(stats: { packetLoss: number; rtt?: number; jitter?: number }): 'excellent' | 'good' | 'fair' | 'poor' {
  const { packetLoss, rtt = 0, jitter = 0 } = stats;

  // If we don't have meaningful stats yet, default to excellent
  if (packetLoss === 0 && rtt === 0 && jitter === 0) {
    return 'excellent';
  }

  if (
    packetLoss <= QUALITY_THRESHOLDS.excellent.packetLoss &&
    rtt <= QUALITY_THRESHOLDS.excellent.rtt &&
    jitter <= QUALITY_THRESHOLDS.excellent.jitter
  ) {
    return 'excellent';
  }
  if (
    packetLoss <= QUALITY_THRESHOLDS.good.packetLoss &&
    rtt <= QUALITY_THRESHOLDS.good.rtt &&
    jitter <= QUALITY_THRESHOLDS.good.jitter
  ) {
    return 'good';
  }
  if (
    packetLoss <= QUALITY_THRESHOLDS.fair.packetLoss &&
    rtt <= QUALITY_THRESHOLDS.fair.rtt &&
    jitter <= QUALITY_THRESHOLDS.fair.jitter
  ) {
    return 'fair';
  }
  return 'poor';
}

export default function useRoomStats(updateIntervalMs: number = 2000) {
  const { room } = useVideoContext();
  const [roomStats, setRoomStats] = useState<RoomStats | null>(null);
  const previousStatsRef = useRef<PreviousStats>({});

  const fetchStats = useCallback(async () => {
    if (!room) return;

    try {
      const statsReports = await room.getStats();
      const now = Date.now();
      const newPreviousStats: PreviousStats = {};

      // Track stats aggregation
      let totalBitrate = 0;
      let totalPacketLoss = 0;
      let packetLossCount = 0;
      let rttSum = 0;
      let rttCount = 0;
      let jitterSum = 0;
      let jitterCount = 0;

      // For local participant stats
      const localTracks: TrackStats[] = [];
      let localRtt = 0;

      // Build a map of trackSid -> participantSid for remote tracks
      const trackToParticipantMap = new Map<string, string>();
      room.participants.forEach((participant: RemoteParticipant) => {
        participant.tracks.forEach((publication: RemoteTrackPublication) => {
          if (publication.trackSid) {
            trackToParticipantMap.set(publication.trackSid, participant.sid);
          }
        });
      });

      // Map to collect remote participant stats
      const remoteParticipantTracksMap = new Map<string, TrackStats[]>();
      room.participants.forEach((participant: RemoteParticipant) => {
        remoteParticipantTracksMap.set(participant.sid, []);
      });

      statsReports.forEach(report => {
        // Debug: log the first report to see RTT values
        if (statsReports.indexOf(report) === 0) {
          console.log('Stats report:', {
            localAudio: report.localAudioTrackStats?.map(s => ({ rtt: s.roundTripTime, jitter: s.jitter })),
            localVideo: report.localVideoTrackStats?.map(s => ({ rtt: s.roundTripTime })),
          });
        }

        // Process LOCAL audio track stats (outgoing)
        report.localAudioTrackStats?.forEach(trackStat => {
          const trackId = trackStat.trackSid || trackStat.trackId || 'local-audio';
          const prevStat = previousStatsRef.current[trackId];
          
          let bitrate = 0;

          if (prevStat && trackStat.bytesSent !== undefined) {
            const timeDiff = (now - prevStat.timestamp) / 1000;
            const bytesDiff = (trackStat.bytesSent || 0) - prevStat.bytesSent;

            if (timeDiff > 0 && bytesDiff >= 0) {
              bitrate = Math.round((bytesDiff * 8) / timeDiff / 1000); // kbps
            }
          }

          newPreviousStats[trackId] = {
            bytesReceived: 0,
            bytesSent: trackStat.bytesSent || 0,
            packetsReceived: 0,
            packetsLost: 0,
            timestamp: now,
          };

          // RTT is already in milliseconds in Twilio SDK
          const rttMs = trackStat.roundTripTime !== null && trackStat.roundTripTime !== undefined 
            ? Math.round(trackStat.roundTripTime) 
            : undefined;
          if (rttMs !== undefined && rttMs > 0 && rttMs < 10000) { // Sanity check: RTT should be < 10s
            localRtt = rttMs;
            rttSum += rttMs;
            rttCount++;
          }

          localTracks.push({
            trackId,
            trackType: 'audio',
            bitrate,
            packetsLost: 0,
            packetsReceived: 0,
            packetLossPercentage: 0,
            roundTripTime: rttMs,
          });

          totalBitrate += bitrate;
        });

        // Process LOCAL video track stats (outgoing)
        report.localVideoTrackStats?.forEach(trackStat => {
          const trackId = trackStat.trackSid || trackStat.trackId || 'local-video';
          const prevStat = previousStatsRef.current[trackId];
          
          let bitrate = 0;

          if (prevStat && trackStat.bytesSent !== undefined) {
            const timeDiff = (now - prevStat.timestamp) / 1000;
            const bytesDiff = (trackStat.bytesSent || 0) - prevStat.bytesSent;

            if (timeDiff > 0 && bytesDiff >= 0) {
              bitrate = Math.round((bytesDiff * 8) / timeDiff / 1000); // kbps
            }
          }

          newPreviousStats[trackId] = {
            bytesReceived: 0,
            bytesSent: trackStat.bytesSent || 0,
            packetsReceived: 0,
            packetsLost: 0,
            timestamp: now,
          };

          // RTT is already in milliseconds in Twilio SDK
          const rttMs = trackStat.roundTripTime !== null && trackStat.roundTripTime !== undefined 
            ? Math.round(trackStat.roundTripTime) 
            : undefined;
          if (rttMs !== undefined && rttMs > 0 && rttMs < 10000) {
            localRtt = rttMs;
            rttSum += rttMs;
            rttCount++;
          }

          localTracks.push({
            trackId,
            trackType: 'video',
            bitrate,
            packetsLost: 0,
            packetsReceived: 0,
            packetLossPercentage: 0,
            roundTripTime: rttMs,
          });

          totalBitrate += bitrate;
        });

        // Process REMOTE audio track stats (incoming)
        report.remoteAudioTrackStats?.forEach(trackStat => {
          const trackId = trackStat.trackSid || trackStat.trackId || 'unknown';
          const prevStat = previousStatsRef.current[trackId];
          
          let bitrate = 0;
          let packetLossPercentage = 0;

          if (prevStat) {
            const timeDiff = (now - prevStat.timestamp) / 1000;
            const bytesDiff = (trackStat.bytesReceived || 0) - prevStat.bytesReceived;
            const packetsReceivedDiff = (trackStat.packetsReceived || 0) - prevStat.packetsReceived;
            const packetsLostDiff = (trackStat.packetsLost || 0) - prevStat.packetsLost;

            if (timeDiff > 0 && bytesDiff >= 0) {
              bitrate = Math.round((bytesDiff * 8) / timeDiff / 1000); // kbps
              const totalPackets = packetsReceivedDiff + Math.max(0, packetsLostDiff);
              packetLossPercentage = totalPackets > 0 ? Math.max(0, (packetsLostDiff / totalPackets) * 100) : 0;
            }
          }

          newPreviousStats[trackId] = {
            bytesReceived: trackStat.bytesReceived || 0,
            bytesSent: 0,
            packetsReceived: trackStat.packetsReceived || 0,
            packetsLost: trackStat.packetsLost || 0,
            timestamp: now,
          };

          // Jitter is in seconds in Twilio SDK, convert to ms
          const jitterMs = trackStat.jitter ? Math.round(trackStat.jitter * 1000) : undefined;
          if (jitterMs !== undefined && jitterMs >= 0 && jitterMs < 5000) { // Sanity check
            jitterSum += jitterMs;
            jitterCount++;
          }

          const trackStats: TrackStats = {
            trackId,
            trackType: 'audio',
            bitrate,
            packetsLost: trackStat.packetsLost || 0,
            packetsReceived: trackStat.packetsReceived || 0,
            packetLossPercentage: Math.round(packetLossPercentage * 100) / 100,
            jitter: jitterMs,
          };

          // Find which participant this track belongs to
          const participantSid = trackToParticipantMap.get(trackStat.trackSid || '');
          if (participantSid && remoteParticipantTracksMap.has(participantSid)) {
            remoteParticipantTracksMap.get(participantSid)!.push(trackStats);
          }

          totalBitrate += bitrate;
          totalPacketLoss += packetLossPercentage;
          packetLossCount++;
        });

        // Process REMOTE video track stats (incoming)
        report.remoteVideoTrackStats?.forEach(trackStat => {
          const trackId = trackStat.trackSid || trackStat.trackId || 'unknown';
          const prevStat = previousStatsRef.current[trackId];
          
          let bitrate = 0;
          let packetLossPercentage = 0;

          if (prevStat) {
            const timeDiff = (now - prevStat.timestamp) / 1000;
            const bytesDiff = (trackStat.bytesReceived || 0) - prevStat.bytesReceived;
            const packetsReceivedDiff = (trackStat.packetsReceived || 0) - prevStat.packetsReceived;
            const packetsLostDiff = (trackStat.packetsLost || 0) - prevStat.packetsLost;

            if (timeDiff > 0 && bytesDiff >= 0) {
              bitrate = Math.round((bytesDiff * 8) / timeDiff / 1000); // kbps
              const totalPackets = packetsReceivedDiff + Math.max(0, packetsLostDiff);
              packetLossPercentage = totalPackets > 0 ? Math.max(0, (packetsLostDiff / totalPackets) * 100) : 0;
            }
          }

          newPreviousStats[trackId] = {
            bytesReceived: trackStat.bytesReceived || 0,
            bytesSent: 0,
            packetsReceived: trackStat.packetsReceived || 0,
            packetsLost: trackStat.packetsLost || 0,
            timestamp: now,
          };

          const trackStats: TrackStats = {
            trackId,
            trackType: 'video',
            bitrate,
            packetsLost: trackStat.packetsLost || 0,
            packetsReceived: trackStat.packetsReceived || 0,
            packetLossPercentage: Math.round(packetLossPercentage * 100) / 100,
          };

          // Find which participant this track belongs to
          const participantSid = trackToParticipantMap.get(trackStat.trackSid || '');
          if (participantSid && remoteParticipantTracksMap.has(participantSid)) {
            remoteParticipantTracksMap.get(participantSid)!.push(trackStats);
          }

          totalBitrate += bitrate;
          totalPacketLoss += packetLossPercentage;
          packetLossCount++;
        });
      });

      previousStatsRef.current = newPreviousStats;

      // Calculate aggregates
      const avgPacketLoss = packetLossCount > 0 ? totalPacketLoss / packetLossCount : 0;
      const avgRtt = rttCount > 0 ? rttSum / rttCount : 0;
      const avgJitter = jitterCount > 0 ? jitterSum / jitterCount : 0;

      // Build local participant stats
      const localParticipantStats: ParticipantStats = {
        participantSid: room.localParticipant.sid,
        participantIdentity: room.localParticipant.identity,
        isLocal: true,
        tracks: localTracks,
        overallQuality: calculateQuality({
          packetLoss: avgPacketLoss,
          rtt: avgRtt,
          jitter: avgJitter,
        }),
      };

      // Build remote participant stats
      const remoteParticipants: ParticipantStats[] = [];
      room.participants.forEach((participant: RemoteParticipant) => {
        const tracks = remoteParticipantTracksMap.get(participant.sid) || [];
        const trackPacketLoss = tracks.length > 0 
          ? tracks.reduce((sum, t) => sum + t.packetLossPercentage, 0) / tracks.length 
          : 0;
        const trackJitter = tracks.filter(t => t.jitter !== undefined).length > 0
          ? tracks.filter(t => t.jitter !== undefined).reduce((sum, t) => sum + (t.jitter || 0), 0) / tracks.filter(t => t.jitter !== undefined).length
          : 0;

        remoteParticipants.push({
          participantSid: participant.sid,
          participantIdentity: participant.identity,
          isLocal: false,
          tracks,
          overallQuality: calculateQuality({
            packetLoss: trackPacketLoss,
            rtt: avgRtt, // Use avg RTT since we measure from local
            jitter: trackJitter,
          }),
        });
      });

      setRoomStats({
        timestamp: now,
        localParticipant: localParticipantStats,
        remoteParticipants,
        aggregate: {
          totalBitrate: Math.round(totalBitrate),
          avgPacketLoss: Math.round(avgPacketLoss * 100) / 100,
          avgRoundTripTime: Math.round(avgRtt),
          avgJitter: Math.round(avgJitter),
        },
      });
    } catch (error) {
      console.error('Error fetching room stats:', error);
    }
  }, [room]);

  useEffect(() => {
    if (!room) {
      setRoomStats(null);
      return;
    }

    // Initial fetch
    fetchStats();

    // Set up interval
    const intervalId = setInterval(fetchStats, updateIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [room, fetchStats, updateIntervalMs]);

  return roomStats;
}
